/* BeadznThreadz premium layer — shared behaviours.
   Exposes window.BZ with: toast(), wishlist, money(), openDrawer(), closeDrawer(), observeReveal(root). */
(function () {
  const config = window.BZ_CONFIG || {};
  const root = document.documentElement;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Money ---------- */
  function money(cents, format) {
    const fmt = format || config.moneyFormat || '₹{{amount}}';
    const value = Number(cents) / 100;
    const withDelimiters = (num, decimals, thousands, dec) => {
      const fixed = num.toFixed(decimals);
      const [whole, frac] = fixed.split('.');
      return whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (frac ? dec + frac : '');
    };
    const out = fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals': return withDelimiters(value, 0, ',', '.');
        case 'amount_with_comma_separator': return withDelimiters(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return withDelimiters(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return withDelimiters(value, 2, "'", '.');
        default: return withDelimiters(value, 2, ',', '.');
      }
    });
    return out.replace(/([.,])00(?!\d)/, '');
  }

  /* ---------- Toast ---------- */
  let toastEl;
  let toastTimer;
  function toast(message, linkHref, linkLabel) {
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'bz-toast';
      toastEl.setAttribute('role', 'status');
      toastEl.setAttribute('aria-live', 'polite');
      document.body.appendChild(toastEl);
    }
    toastEl.textContent = message;
    if (linkHref) {
      const a = document.createElement('a');
      a.href = linkHref;
      a.textContent = linkLabel || 'View';
      toastEl.appendChild(a);
    }
    requestAnimationFrame(() => toastEl.classList.add('is-visible'));
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => toastEl.classList.remove('is-visible'), 3200);
  }

  /* ---------- Wishlist (localStorage, product handles) ---------- */
  const WL_KEY = 'bz:wishlist';
  const wishlist = {
    read() {
      try {
        const v = JSON.parse(localStorage.getItem(WL_KEY) || '[]');
        return Array.isArray(v) ? v : [];
      } catch (e) {
        return [];
      }
    },
    write(list) {
      try {
        localStorage.setItem(WL_KEY, JSON.stringify(list));
      } catch (e) {}
      document.dispatchEvent(new CustomEvent('bz:wishlist:change', { detail: { items: list } }));
    },
    has(handle) {
      return this.read().includes(handle);
    },
    toggle(handle) {
      const list = this.read();
      const i = list.indexOf(handle);
      if (i > -1) list.splice(i, 1);
      else list.unshift(handle);
      this.write(list);
      return i === -1;
    },
    remove(handle) {
      this.write(this.read().filter((h) => h !== handle));
    },
    clear() {
      this.write([]);
    },
    sync(scope) {
      const items = this.read();
      (scope || document).querySelectorAll('[data-bz-wishlist-toggle]').forEach((btn) => {
        const on = items.includes(btn.dataset.handle);
        btn.setAttribute('aria-pressed', on ? 'true' : 'false');
        const label = on ? btn.dataset.labelRemove : btn.dataset.labelAdd;
        if (label) btn.setAttribute('aria-label', label);
      });
      document.querySelectorAll('[data-bz-wishlist-count]').forEach((el) => {
        el.textContent = items.length;
        el.hidden = items.length === 0;
      });
    },
  };

  document.addEventListener('click', (event) => {
    const btn = event.target.closest('[data-bz-wishlist-toggle]');
    if (!btn) return;
    event.preventDefault();
    event.stopPropagation();
    const added = wishlist.toggle(btn.dataset.handle);
    btn.classList.remove('is-popping');
    void btn.offsetWidth;
    btn.classList.add('is-popping');
    const wl = config.wishlist || {};
    toast(added ? wl.added || 'Saved to wishlist' : wl.removed || 'Removed from wishlist', added && wl.page ? wl.page : null, 'View wishlist');
  });
  document.addEventListener('bz:wishlist:change', () => wishlist.sync());
  window.addEventListener('storage', (e) => {
    if (e.key === WL_KEY) wishlist.sync();
  });

  /* ---------- Reveal on scroll + thread drawing ---------- */
  const style = config.reveal || 'fade-up';
  if (style !== 'none' && !reduceMotion && 'IntersectionObserver' in window) {
    root.classList.add('bz-reveal-on', 'bz-reveal-' + style);
  }
  if (config.threads && !reduceMotion && 'IntersectionObserver' in window) {
    root.classList.add('bz-threads-animate');
  }

  const io =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              entry.target.classList.add(entry.target.matches('[data-bz-draw]') ? 'is-drawn' : 'is-revealed');
              io.unobserve(entry.target);
            });
          },
          { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
        )
      : null;

  function observeReveal(scope) {
    const base = scope || document;
    base.querySelectorAll('[data-bz-reveal]:not(.is-revealed)').forEach((el) => {
      if (io) io.observe(el);
      else el.classList.add('is-revealed');
    });
    base.querySelectorAll('.bz-thread[data-bz-draw]:not(.is-drawn)').forEach((svg) => {
      const path = svg.querySelector('path');
      if (path && path.getTotalLength) {
        try {
          svg.style.setProperty('--bz-len', Math.ceil(path.getTotalLength()));
        } catch (e) {}
      }
      if (io) io.observe(svg);
      else svg.classList.add('is-drawn');
    });
  }

  /* ---------- Drawer helper (any element with class bz-drawer) ---------- */
  let lastFocus;
  function openDrawer(el) {
    if (!el) return;
    lastFocus = document.activeElement;
    el.setAttribute('open', '');
    el.removeAttribute('aria-hidden');
    root.classList.add('bz-lock');
    const focusable = el.querySelector('[autofocus], button, [href], input, select, textarea');
    if (focusable) setTimeout(() => focusable.focus(), 50);
    el.dispatchEvent(new CustomEvent('bz:drawer:open', { bubbles: true }));
  }
  function closeDrawer(el) {
    if (!el) return;
    el.removeAttribute('open');
    el.setAttribute('aria-hidden', 'true');
    if (!document.querySelector('.bz-drawer[open]')) root.classList.remove('bz-lock');
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }
  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-bz-open]');
    if (opener) {
      event.preventDefault();
      openDrawer(document.getElementById(opener.dataset.bzOpen));
      return;
    }
    const closer = event.target.closest('[data-bz-close]');
    if (closer) {
      event.preventDefault();
      closeDrawer(closer.closest('.bz-drawer'));
    }
  });
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const open = document.querySelectorAll('.bz-drawer[open]');
    if (open.length) closeDrawer(open[open.length - 1]);
  });

  /* ---------- Generic carousel arrows: [data-bz-prev]/[data-bz-next] with data-target track id ---------- */
  document.addEventListener('click', (event) => {
    const btn = event.target.closest('[data-bz-prev], [data-bz-next]');
    if (!btn) return;
    const track = document.getElementById(btn.dataset.target);
    if (!track) return;
    const item = track.firstElementChild;
    const step = item ? item.getBoundingClientRect().width + parseFloat(getComputedStyle(track).columnGap || 0) : track.clientWidth;
    track.scrollBy({ left: btn.hasAttribute('data-bz-prev') ? -step : step, behavior: reduceMotion ? 'auto' : 'smooth' });
  });

  /* ---------- Smooth anchors ---------- */
  if (config.smoothScroll && !reduceMotion) {
    document.addEventListener('click', (event) => {
      const a = event.target.closest('a[href^="#"]');
      if (!a || a.getAttribute('href').length < 2) return;
      const target = document.getElementById(decodeURIComponent(a.getAttribute('href').slice(1)));
      if (!target) return;
      event.preventDefault();
      const offset = parseInt(getComputedStyle(root).getPropertyValue('--header-height'), 10) || 0;
      window.scrollTo({ top: target.getBoundingClientRect().top + window.scrollY - offset - 16, behavior: 'smooth' });
      history.replaceState(null, '', a.getAttribute('href'));
    });
  }

  /* ---------- Back to top ---------- */
  function initBackToTop() {
    const btn = document.querySelector('[data-bz-back-to-top]');
    if (!btn) return;
    let ticking = false;
    window.addEventListener(
      'scroll',
      () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          btn.classList.toggle('is-visible', window.scrollY > window.innerHeight * 1.5);
          ticking = false;
        });
      },
      { passive: true }
    );
    btn.addEventListener('click', () => window.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' }));
  }

  /* ---------- Lazy videos: <video data-bz-lazy-video> with <source data-src> ---------- */
  function observeVideos(scope) {
    const vids = (scope || document).querySelectorAll('video[data-bz-lazy-video]:not([data-loaded])');
    if (!vids.length) return;
    const vio =
      'IntersectionObserver' in window
        ? new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                const v = entry.target;
                if (entry.isIntersecting) {
                  if (!v.dataset.loaded) {
                    v.querySelectorAll('source[data-src]').forEach((s) => (s.src = s.dataset.src));
                    v.load();
                    v.dataset.loaded = 'true';
                  }
                  if (v.hasAttribute('data-autoplay') && !reduceMotion) v.play().catch(() => {});
                } else if (!v.paused && v.hasAttribute('data-autoplay')) {
                  v.pause();
                }
              });
            },
            { rootMargin: '200px 0px' }
          )
        : null;
    vids.forEach((v) => (vio ? vio.observe(v) : v.querySelectorAll('source[data-src]').forEach((s) => (s.src = s.dataset.src))));
  }

  function init(scope) {
    observeReveal(scope);
    observeVideos(scope);
    wishlist.sync(scope);
  }

  window.BZ = { toast, wishlist, money, openDrawer, closeDrawer, observeReveal, observeVideos, init };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      init();
      initBackToTop();
    });
  } else {
    init();
    initBackToTop();
  }

  /* Theme editor: re-init when sections are added/re-rendered */
  document.addEventListener('shopify:section:load', (e) => init(e.target));
  /* Dawn re-renders product grids via section rendering (filters, pagination) */
  document.addEventListener('bz:dom:updated', (e) => init(e.target || document));
  if ('MutationObserver' in window) {
    const grid = () => document.getElementById('ProductGridContainer');
    const target = grid();
    if (target) new MutationObserver(() => init(target)).observe(target, { childList: true, subtree: true });
  }
})();
